import { Component } from "react";
import { bool, func, number, string } from "prop-types";
import styled from "styled-components/macro";

import {
  FS_CDN,
  FS_WATERMARK_PENDING,
  FS_WATERMARK_SOLD,
  FS_WATERMARK_SPECIAL,
} from "../../constants";

import Picture from "../../styles/components/Picture";

class FilestackImage extends Component {
  constructor(props) {
    super(props);

    this.getSource = this.getSource.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleLoad = this.handleLoad.bind(this);

    this._isMounted = false;

    this.state = {
      status: "loading",
      x1: null,
      x2: null,
    };
  }

  /**
   * Get image source URL
   * @param {string} handle
   * @param {number} height
   * @param {number} width
   * @param {number} resolution
   * @public
   */
  getSource(handle, height, width, resolution) {
    const { pending, sold, special } = this.props;
    let watermarkPending = "";
    let watermarkSold = "";
    let watermarkSpecial = "";

    if (pending && !!FS_WATERMARK_PENDING) {
      let scalePending = !!width ? width * 0.01650165 : 5;

      if (!!width && !!height) {
        const ratio = height / width;

        if (ratio < 0.5) {
          scalePending = scalePending * ratio;
        }
      }

      if (!!resolution) {
        scalePending = scalePending * resolution;
      }

      scalePending = Math.ceil(scalePending);

      watermarkPending = `/watermark=file:${FS_WATERMARK_PENDING},size:${scalePending},position:[middle,center]`;
    }

    if (sold) {
      let scaleSold = !!width ? width * 0.01650165 : 5;

      if (!!width && !!height) {
        const ratio = height / width;

        if (ratio < 0.5) {
          scaleSold = scaleSold * ratio;
        }
      }

      if (!!resolution) {
        scaleSold = scaleSold * resolution;
      }

      scaleSold = Math.ceil(scaleSold);

      watermarkSold = `/watermark=file:${FS_WATERMARK_SOLD},size:${scaleSold},position:[middle,center]`;
    }

    if (special && !!FS_WATERMARK_SPECIAL) {
      let scaleSpecial = !!width ? width * 0.03125 : 9;

      if (!!width && !!height) {
        const ratio = height / width;

        if (ratio < 1) {
          scaleSpecial =
            ratio < 0.56 ? scaleSpecial * ratio * 1.5 : scaleSpecial * ratio;
        }
      }

      if (!!resolution) {
        scaleSpecial = scaleSpecial * resolution;
      }

      scaleSpecial = Math.ceil(scaleSpecial);

      watermarkSpecial = `/watermark=file:${FS_WATERMARK_SPECIAL},size:${scaleSpecial},position:[top,left]`;
    }

    return `${FS_CDN}/rotate=deg:exif/auto_image/resize=width:${
      width * resolution
    },height:${
      height * resolution
    },fit:crop${watermarkPending}${watermarkSold}${watermarkSpecial}/compress/cache=expiry:max/${handle}`;
  }

  /**
   * Handle status if image throws an error
   * @public
   */
  handleError() {
    if (this._isMounted && this.state.status !== "error") {
      this.setState({ status: "error" });

      if (this.props.errorCallback) {
        this.props.errorCallback(this.props.handle);
      }
    }
  }

  /**
   * Handle status if image loads successfully
   * @public
   */
  handleLoad() {
    if (this._isMounted && this.state.status !== "done") {
      this.setState({ status: "done" });
    }
  }

  componentDidMount() {
    this._isMounted = true;

    const { getSource } = this;
    const { handle, height, width } = this.props;

    this.setState({
      x1: getSource(handle, height, width, 1),
      x2: getSource(handle, height, width, 2),
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { handleError, handleLoad } = this;
    const { alt, className, height, theme, width } = this.props;
    const { x1, x2, status } = this.state;

    return (
      <Picture
        alt={alt}
        className={className}
        data-status={status}
        theme={theme}
      >
        <source srcSet={`${x1} 1x, ${x2} 2x`} width={width} height={height} />
        <Image
          alt={alt}
          draggable={false}
          onError={handleError}
          onLoad={handleLoad}
          src={x1}
          width={width}
          height={height}
        />
      </Picture>
    );
  }
}

const Image = styled.img`
  display: block;
  height: auto;
  max-width: 100%;
  width: 100%;
`;

FilestackImage.propTypes = {
  alt: string,
  className: string,
  errorCallback: func,
  handle: string.isRequired,
  height: number,
  pending: bool,
  sold: bool,
  special: bool,
  theme: string,
  width: number,
};

FilestackImage.defaultProps = {
  alt: "image",
  height: 100,
  pending: false,
  sold: false,
  special: false,
  width: 100,
};

export default FilestackImage;
